// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingsCompareIcon } from "@/components/icons";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";

interface CompareStoreDialogProps {
  defaultStoreName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CompareStoreDialog({ defaultStoreName, open, onOpenChange }: CompareStoreDialogProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const [storeNameA, setStoreNameA] = useState(defaultStoreName);
  const [storeNameB, setStoreNameB] = useState("");

  const prevOpen = useRef(false);
  useEffect(
    function resetSelectionsOnOpen() {
      if (open && !prevOpen.current) {
        setStoreNameA(defaultStoreName);
        setStoreNameB("");
      }
      prevOpen.current = open;
    },
    [open, defaultStoreName],
  );

  const { data: context } = useBackofficeContext();
  const allOptions = useMemo(
    () => (context?.stores ?? []).map((s) => ({ label: s.display, value: s.value })),
    [context],
  );

  const optionsA = useMemo(() => allOptions.filter((o) => o.value !== storeNameB), [allOptions, storeNameB]);
  const optionsB = useMemo(() => allOptions.filter((o) => o.value !== storeNameA), [allOptions, storeNameA]);

  function handleCompare() {
    if (!storeNameA || !storeNameB) return;
    navigate({
      to: "/stores/compare/$storeNameA/$storeNameB",
      params: { storeNameA, storeNameB },
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id="dialog-compare-stores" className="sm:max-w-[37rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane icon={SettingsCompareIcon} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("stores.compare.dialog.title")}</DialogTitle>
              <DialogDescription>
                {t("stores.compare.dialog.description", "Select two stores to compare their settings.")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("stores.compare.dialog.sourceLabel")}</Label>
                <VirtualSelectPopover
                  value={storeNameA}
                  onChange={setStoreNameA}
                  options={optionsA}
                  placeholder={t("stores.compare.dialog.pickStore")}
                  searchPlaceholder={t("stores.list.searchPlaceholder")}
                  emptyText={t("common.noResults")}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("stores.compare.dialog.targetLabel")}</Label>
                <VirtualSelectPopover
                  value={storeNameB}
                  onChange={setStoreNameB}
                  options={optionsB}
                  placeholder={t("stores.compare.dialog.pickStore")}
                  searchPlaceholder={t("stores.list.searchPlaceholder")}
                  emptyText={t("common.noResults")}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button onClick={handleCompare} disabled={!storeNameA || !storeNameB}>
            <SettingsCompareIcon className="h-4 w-4" />
            {t("stores.compare.dialog.compare")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
